/**
 * What a report is being built from, as a value the URL can hold.
 *
 * The reports page picks its own county, its own scope and its own filters
 * rather than inheriting whatever the dashboard happened to be showing — so
 * this is where those choices live, and it gets the treatment `route.ts`,
 * `views.ts`, `alerts.ts` and `pricing.ts` get: pure functions with tests,
 * because what the page offers is a product decision and not an accident of
 * `if` placement.
 *
 * Three rules hold it together.
 *
 * **The parameters live in the URL, not in component state.** A quote is a
 * price, and a price has to survive a reload, a Back and being sent to
 * somebody else. That is the rule the selected county already follows on the
 * dashboard, applied to the whole basket.
 *
 * **`scope` is offered against what the county actually holds.** Manatee,
 * Charlotte and Duval publish parcels and no buildings at all — not "none
 * matching your filters", none — so a Buildings report there would be a
 * refusal dressed as a button. `heldViews` already knows, and this reuses it
 * rather than keeping a second idea of which counties have what.
 *
 * **A filter that cannot apply is not sent.** `flag` means nothing to a
 * building and `min_rate` means nothing to a parcel, so `toQuery` drops them
 * by scope. Sending them anyway would work — the server ignores what its
 * filter object has no field for — and would put a control in the URL that
 * the price does not respond to, which reads as the price being wrong.
 */

import type { County } from "../api/types";
import { heldViews, type Held } from "./views";

export type ReportScope = "buildings" | "units" | "both";

export interface ReportParams {
  /** Five-digit FIPS. Empty is a real state: nothing is chosen yet. */
  county: string;
  scope: ReportScope;
  min_score: number;
  milestone_only: boolean;
  unsafe_only: boolean;
  min_rate: number;
  /**
   * Signals to keep, matched any-of. Empty is "no signal constraint", which is
   * not the same as all of them — a record carrying none still belongs in an
   * unfiltered report.
   */
  signals: string[];
  search: string;
}

export const EMPTY_REPORT_PARAMS: ReportParams = {
  county: "",
  scope: "buildings",
  min_score: 0,
  milestone_only: false,
  unsafe_only: false,
  min_rate: 0,
  signals: [],
  search: "",
};

/**
 * The signals a **building** row can be filtered on.
 *
 * Five of the twelve, and a copy of `BUILDING_SIGNAL_COLUMNS` on the server —
 * which is a duplication worth being explicit about. It is not a second source
 * of truth for *what a signal means*; that stays in `WEIGHTS` and arrives via
 * `/api/scoring`. It is a copy of which ones `buildings` rolls up into a
 * column, so the control can be disabled before the click rather than the
 * server refusing after it. The server refuses regardless, so the worst a
 * stale copy here can do is offer an option that comes back 400 — never let a
 * wrong filter through.
 */
export const BUILDING_SIGNALS = [
  "unsafe_structure",
  "expired_permit",
  "lien_recorded",
  "absentee",
  "milestone_due",
];

const SCOPES: ReportScope[] = ["buildings", "units", "both"];

/**
 * Read the basket out of a query string.
 *
 * Anything unreadable falls back to the empty value rather than throwing. A
 * report URL gets edited by hand, shortened, and pasted with a character
 * missing, and the right answer to `min_score=abc` is an unfiltered basket
 * somebody can see and correct — not a blank page.
 */
export function fromQuery(params: Record<string, string>): ReportParams {
  const scope = params.scope as ReportScope;
  return {
    county: (params.county ?? "").trim(),
    scope: SCOPES.includes(scope) ? scope : "buildings",
    min_score: positive(params.min_score),
    milestone_only: params.milestone_only === "true",
    unsafe_only: params.unsafe_only === "true",
    min_rate: rate(params.min_rate),
    // A bare `flag` is the dashboard's single-select, and older shared links
    // carry one. Folded in rather than kept as a second control: two ways to
    // say "which signals" on one page is two things to keep in step, and a
    // filter with no chip on screen is a filter nobody can see is applied.
    signals: list([params.signals, params.flag].filter(Boolean).join(",")),
    search: (params.search ?? "").trim(),
  };
}

/**
 * The basket as query parameters, dropping everything that cannot apply.
 *
 * The same object is sent to the quote, the preview and the generate call, so
 * the three cannot describe different baskets — which is the client-side half
 * of the rule `basket()` enforces on the server.
 */
export function toQuery(
  params: ReportParams,
): Record<string, string | number | boolean> {
  const query: Record<string, string | number | boolean> = {
    county: params.county,
    scope: params.scope,
  };
  if (params.min_score) query.min_score = params.min_score;
  if (params.search) query.search = params.search;
  if (params.scope !== "units") {
    if (params.milestone_only) query.milestone_only = true;
    if (params.unsafe_only) query.unsafe_only = true;
    if (params.min_rate) query.min_rate = params.min_rate;
  }
  // Comma-separated rather than repeated, so the whole basket stays one flat
  // readable query string — this URL is meant to be shared, and
  // `?signals=a&signals=b&signals=c` is not.
  if (params.signals.length) query.signals = params.signals.join(",");
  return query;
}

/** The basket as a URL, so the page's own address is the shareable price. */
export function toSearch(params: ReportParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(toQuery(params))) {
    if (value === "" || value === false) continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

export interface ScopeOption {
  value: ReportScope;
  label: string;
  /** True where the county holds no row of this shape at all. */
  disabled: boolean;
  /** Why, for the control's title. Empty when it is offered. */
  reason: string;
}

/**
 * The three scopes, with the ones this county cannot fill marked.
 *
 * Disabled rather than hidden, which is this codebase's standing rule for a
 * control a plan or a county cannot reach: a missing option reads as a feature
 * the product lacks, where a disabled one carries its reason. And the reason
 * names the county, because "no buildings" without a subject reads as a
 * problem with the request.
 */
export function scopeOptions(entry?: Held): ScopeOption[] {
  const held = heldViews(entry);
  const where = entry?.name ? `${entry.name} County` : "This county";
  return [
    {
      value: "buildings",
      label: "Buildings",
      disabled: !held.buildings,
      reason: held.buildings
        ? ""
        : `${where} publishes no building-level records we can source.`,
    },
    {
      value: "units",
      label: "Parcels",
      disabled: !held.units,
      reason: held.units
        ? ""
        : `${where} publishes no parcel records we can source.`,
    },
    {
      value: "both",
      label: "Both",
      disabled: !held.buildings || !held.units,
      reason:
        held.buildings && held.units
          ? ""
          : `${where} publishes only one of the two, so a combined report ` +
            `would be the same document under a wider heading.`,
    },
  ];
}

/**
 * The scope to actually price.
 *
 * `resolveView`'s rule, with the third option added: fall through to the shape
 * the county has rather than pricing one it cannot fill. A county holding
 * neither keeps the request as made — there is nothing better to switch to,
 * and an honest empty basket beats a silent substitution.
 */
export function resolveScope(requested: ReportScope, entry?: Held): ReportScope {
  const options = scopeOptions(entry);
  const asked = options.find((option) => option.value === requested);
  if (asked && !asked.disabled) return requested;
  const fallback = options.find((option) => !option.disabled);
  return fallback ? fallback.value : requested;
}

/**
 * The counties a report can be run against.
 *
 * Live ones only, named. A `PLANNED` county is a public claim about a roadmap
 * and holds no rows, so offering it here would be an empty document with a
 * price on it.
 */
export function reportableCounties(counties: County[]): County[] {
  return counties
    .filter((entry) => entry.status === "live")
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * What is missing before this basket can be priced at all.
 *
 * Only the county, today — everything else has a working default. It is a
 * function rather than a `!county` check at the call site so the page has one
 * place to say what an unfinished basket is waiting for.
 */
export function missingChoice(params: ReportParams): string {
  if (!params.county) {
    return "A report covers one county. Choose one to see what it costs.";
  }
  return "";
}

/** A comma-separated list, de-duplicated, with the order the user chose kept. */
function list(raw: string | undefined): string[] {
  const seen = new Set<string>();
  for (const part of (raw ?? "").split(",")) {
    const name = part.trim();
    if (name) seen.add(name);
  }
  return [...seen];
}

function positive(raw: string | undefined): number {
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

function rate(raw: string | undefined): number {
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(1, value);
}

/**
 * The dashboard's filters, as a report URL.
 *
 * Lives here rather than in `FilterRail` — where it used to, when the rail was
 * what linked to a report — because the link moved to the masthead and a URL
 * builder that lives inside whichever component happens to render the button
 * moves every time the button does. Both ends of this trip are report
 * concerns: `fromQuery` reads what this writes.
 *
 * Empty values are dropped rather than sent. The server's defaults are the
 * same defaults, and a URL carrying `min_score=0&search=` is a price somebody
 * cannot read at a glance.
 */
export function reportQuery(
  view: "buildings" | "units",
  filters: {
    min_score: number;
    milestone_only: boolean;
    unsafe_only: boolean;
    min_rate: number;
    flag: string;
    search: string;
  },
  county: string,
): string {
  return toSearch({
    ...EMPTY_REPORT_PARAMS,
    county,
    scope: view,
    min_score: filters.min_score,
    milestone_only: filters.milestone_only,
    unsafe_only: filters.unsafe_only,
    min_rate: filters.min_rate,
    // The rail's one flag arrives as one selected chip. Carried as `signals`
    // rather than `flag` so it is *visible* on arrival — a filter applied to
    // the price with no control showing it reads as the price being wrong.
    // Dropped for a buildings report the same way any unanswerable signal is.
    signals: keepAnswerable(filters.flag ? [filters.flag] : [], view),
    search: filters.search.trim(),
  });
}

export interface SignalOption {
  value: string;
  label: string;
  /** True where this scope or this county cannot answer for it. */
  disabled: boolean;
  reason: string;
}

/**
 * Which signals this report can be narrowed to, and why the rest are refused.
 *
 * Two independent constraints, and they refuse for different reasons — so they
 * carry different sentences rather than one vague "unavailable".
 *
 * **The county.** `SIGNALS_BY_COUNTY` is the source of truth for what a county
 * can evidence, and it varies enormously: Miami-Dade declares twelve, Pasco
 * declares one. A signal the county cannot evidence is not offered, because
 * filtering on it would return nothing and read as "there are none here"
 * rather than "nobody publishes this here". Derived from the county's own list
 * rather than from a list of county names — `scoreScope.ts`' rule, and the
 * masthead has already gone stale once by hard-coding what a county could not
 * do.
 *
 * **The scope.** A parcel row carries every signal in its `flags` string; a
 * building row carries a rolled-up count of five of them. So the same signal
 * is filterable on parcels and not on buildings, and a combined report is
 * bound by the stricter of the two.
 *
 * Disabled and explained, never hidden: an option that vanishes reads as a
 * feature the product lacks, where a disabled one says which county or which
 * scope it is about.
 */
export function signalOptions(
  scope: ReportScope,
  countySignals: string[] | undefined,
  labels: Record<string, string>,
  countyName?: string,
): SignalOption[] {
  // Undefined means the registry has not loaded. Offer the full vocabulary
  // rather than an empty list — a control must never look empty because a
  // request is in flight.
  const evidenced = countySignals ?? Object.keys(labels);
  const where = countyName ? `${countyName} County` : "This county";
  const onBuildings = scope !== "units";

  return Object.keys(labels)
    .filter((signal) => evidenced.includes(signal) || !countySignals)
    .map((signal) => {
      const rolled = BUILDING_SIGNALS.includes(signal);
      const disabled = onBuildings && !rolled;
      return {
        value: signal,
        label: labels[signal] ?? signal,
        disabled,
        reason: disabled
          ? `A building record carries a rolled-up count of five signals and ` +
            `this is not one of them. Report on parcels to filter by it.`
          : "",
      };
    })
    .concat(
      // Named rather than silently absent: "no unsafe-structure option" and
      // "this county publishes no case feed" are different findings, and only
      // the second is true.
      countySignals
        ? Object.keys(labels)
            .filter((signal) => !evidenced.includes(signal))
            .map((signal) => ({
              value: signal,
              label: labels[signal] ?? signal,
              disabled: true,
              reason: `${where} publishes nothing we can evidence this with.`,
            }))
        : [],
    );
}

/**
 * The chosen signals, minus any this scope cannot answer for.
 *
 * Applied when the scope changes rather than left for the server to refuse:
 * switching a parcels report to buildings would otherwise carry an unfilterable
 * signal along and turn the whole page into a 400 nobody asked for. Dropping is
 * safe *here* and only here — the selection is visible on screen, so a removed
 * chip is seen, unlike a term dropped from a WHERE clause.
 */
export function keepAnswerable(signals: string[], scope: ReportScope): string[] {
  if (scope === "units") return signals;
  return signals.filter((signal) => BUILDING_SIGNALS.includes(signal));
}
